export const ACTION_TYPES = {
  openAddModalActionType: 'OPEN_ADD_AOR_RUN_MODAL',
  openEditModalActionType: 'OPEN_EDIT_AOR_RUN_MODAL',
  closeAddModalActionType: 'CLOSE_ADD_AOR_RUN_MODAL',
  closeEditModalActionType: 'CLOSE_EDIT_AOR_RUN_MODAL',
  initFetchActionType: 'FETCH_AOR_RUNS_INIT',
  successFetchActionType: 'FETCH_AOR_RUNS_SUCCESS',
  failureFetchActionType: 'FETCH_AOR_RUNS_FAILURE',
  initAddActionType: 'ADD_AOR_RUN_INIT',
  successAddActionType: 'ADD_AOR_RUN_SUCCESS',
  failureAddActionType: 'ADD_AOR_RUN_FAILURE',
  initUpdateActionType: 'UPDATE_AOR_RUN_INIT',
  successUpdateActionType: 'UPDATE_AOR_RUN_SUCCESS',
  failureUpdateActionType: 'UPDATE_AOR_RUN_FAILURE'
};

export const headCells = [
  { id: 'aorRequestId', label: 'AOR Request', minWidth: 260 },
  { id: 'status', label: 'Status', minWidth: 120 },
  { id: 'policies', label: 'Policies', minWidth: 110 },
  { id: 'offers', label: 'Offers', minWidth: 90 },
  { id: 'members', label: 'Members', minWidth: 90 },
  { id: 'staffUsers', label: 'Staff', minWidth: 80 },
  { id: 'nratScheduled', label: 'NRAT', minWidth: 80 },
  { id: 'memberPolicyAffinityMismatch', label: 'Mismatches', minWidth: 110 },
  { id: 'createdAt', label: 'Previewed', minWidth: 170 },
  { id: 'approvedBy', label: 'Approved By', minWidth: 170 },
  { id: 'artifacts', label: 'Snapshot', minWidth: 110 }
];

/** The six snapshot CSVs, in the order the preview writes them. `output` must match the S3 basename. */
export const ARTIFACT_OUTPUTS = [
  { output: 'policies', label: 'Policies (policyId, accountId, affinity, inForce)' },
  { output: 'offers', label: 'Offers (offerId, affinity, offerDate)' },
  { output: 'members', label: 'Members (accountId, affinity, stem, email)' },
  { output: 'staff-users', label: 'Staff users (loginId, email, affinity fields)' },
  { output: 'nrat-reversals', label: 'NRAT reversals (policyId, recheckId, status)' },
  { output: 'affinity-mismatches', label: 'Member/policy mismatches (accountId, policyId, both affinities)' }
];

/** Order and wording used by the approve dialog. */
export const COUNT_LABELS = {
  policies: 'Policies',
  offers: 'Offers (last 30 days)',
  members: 'Members',
  staffUsers: 'Staff users',
  nratScheduled: 'Scheduled NRAT letters to reverse',
  memberPolicyAffinityMismatch: 'Member/policy affinity mismatches'
};

/** Approving resumes the parked machine, so approve and apply are one gesture and one predicate. */
export const canApproveAndApply = (run) => run?.status === 'previewed' && !run?.isStale && !run?.isSuperseded;

// `isStale` is in here too: a stale previewed row fails canApproveAndApply and would offer nothing.
// Not `applying` (mid-flight) or a live `approved` (a new preview would supersede the pending approval).
const PREVIEWABLE_STATUSES = ['previewed', 'preview_failed', 'applied', 'partially_applied', 'failed', 'stale'];
/**
 * A new preview starts a whole fresh cycle, and only from the NEWEST run of its AOR request.
 * `isSuperseded` is server-derived (exactly one row per request has it false), because a newer run
 * can sit on another page, so it cannot be inferred from the rows in hand. Absent on an older
 * core-resources, which degrades to the previous every-row behaviour rather than hiding the action
 * outright.
 */
// A preview that never finished: the row exists only so the request has something to retry from.
export const isPreviewFailed = (run) => run?.status === 'preview_failed';

export const canRunPreview = (run) =>
  !!run && !run.isSuperseded && (PREVIEWABLE_STATUSES.includes(run.status) || !!run.isStale);

/** Shared with the Update Policy Rep tools; the approve dialog defaults to sales_rep (checklist parity). */
export { REP_UPDATE_TYPE_OPTIONS } from 'tools/tasks/policy-actions/rep-update-types';

// No hour value here on purpose: only the server knows the window, and only its refusals quote it.
export const STALE_REASON_TEXT = {
  expired: 'Preview expired',
  mappings_changed: 'Mappings changed since preview'
};

/** One copy of the status wording — the runs table and the run detail page both render it. */
export const statusText = (run) =>
  run?.isStale ? `${run.status} — ${STALE_REASON_TEXT[run.staleReason] ?? 'stale'}` : run?.status;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * One search box for both: operators work in affinity codes, but a request id is what the preview
 * and the CSVs are keyed on, so accept either and route on shape.
 */
export const buildRunsSearchFilter = (searchValue) => {
  const term = (searchValue || '').trim();
  if (!term) {
    return {};
  }
  return UUID_RE.test(term) ? { aorRequestId: term } : { affinity: term.toUpperCase() };
};

export const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};
